import type { AppNotification } from '@discovr/types';

import { normalizeSampleActors } from './notificationActors';

// Display-time clubbing for the Activities tab, applied WITHIN one day
// section (the industry aggregation key — campaign + type + day; the day
// axis is already provided by groupByDay upstream). Clubs never cross
// campaigns: the key embeds campaign_id, so "5 scripts in campaign A" and
// "2 scripts in campaign B" stay two clubs.
//
// Only per-creator activity docs (count <= 1) participate. Legacy
// write-time-coalesced docs (count > 1) keep their own card — their members
// are not separately addressable, so folding them into a client-side club
// would break per-item read/delete. Campaign-less docs (join requests)
// never club.

export interface NotificationClub {
  /** campaign_id + type — the aggregation key (day is implicit). */
  key: string;
  /** 2+ member docs in list order; each is individually read/deletable. */
  items: AppNotification[];
}

export type DayEntry =
  | { kind: 'single'; item: AppNotification }
  | { kind: 'club'; club: NotificationClub };

function clubKeyOf(n: AppNotification): string | null {
  if (!n.campaign_id || n.count > 1) return null;
  return `${n.campaign_id}|${n.type}`;
}

/**
 * Groups one day-section's activity items into clubs of 2+ same
 * campaign+type docs, keeping singles as-is. A club sits at its first
 * member's position; members keep their relative order.
 */
export function clubDayItems(items: AppNotification[]): DayEntry[] {
  const clubs = new Map<string, NotificationClub>();
  const entries: DayEntry[] = [];

  for (const item of items) {
    const key = clubKeyOf(item);
    if (!key) {
      entries.push({ kind: 'single', item });
      continue;
    }
    const existing = clubs.get(key);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    const club: NotificationClub = { key, items: [item] };
    clubs.set(key, club);
    entries.push({ kind: 'club', club });
  }

  // A "club" of one collapses back to a plain single card.
  return entries.map((e): DayEntry => {
    if (e.kind === 'club') {
      const only = e.club.items.length === 1 ? e.club.items[0] : undefined;
      if (only) return { kind: 'single', item: only };
    }
    return e;
  });
}

/**
 * The creator name a per-creator doc identifies its subject by: the DTO's
 * creator_name when the BE could resolve it, else the doc's own (single)
 * sample actor — older docs predate the payload creator_name enrichment but
 * always carried the actor sample.
 */
export function memberName(n: AppNotification): string | undefined {
  return n.creator_name ?? normalizeSampleActors(n.sample_actors)[0]?.name;
}

/**
 * Named-actor headline for a club — "Asha, Ravi and 3 others uploaded
 * scripts". Counts DISTINCT creators, not member docs: names dedupe via
 * memberName (first-seen order) and each name-less doc counts as one
 * unknown creator, so one creator's two docs never render "2 creators".
 * A club that turns out to be a single creator falls back to that member's
 * own sentence (batch labels are plural-only); a doc with no batch_label at
 * all falls back to the first member's copy — which under-reports the rest
 * of the club, accepted as transitional (pre-copy-fields docs only).
 */
export function clubHeadline(club: NotificationClub): string {
  const first = club.items[0];
  if (!first) return '';
  const label = first.batch_label;

  if (!label) return first.message || first.title;

  const names: string[] = [];
  let unnamed = 0;
  for (const item of club.items) {
    const name = memberName(item);
    if (!name) unnamed += 1;
    else if (!names.includes(name)) names.push(name);
  }
  const creators = names.length + unnamed;

  if (creators < 2) return first.message || first.title;
  if (names.length === 0) return `${creators} creators ${label}`;
  if (names.length === 1) {
    const rest = creators - 1;
    return `${names[0]} and ${rest} ${rest === 1 ? 'other' : 'others'} ${label}`;
  }
  const rest = creators - 2;
  if (rest <= 0) return `${names[0]} and ${names[1]} ${label}`;
  return `${names[0]}, ${names[1]} and ${rest} ${rest === 1 ? 'other' : 'others'} ${label}`;
}
